/**
 * Analytics Page
 *
 * Progress tracking with:
 * - Overview stats from Supabase
 * - Weekly/Monthly volume bar chart
 * - Workout type distribution pie chart
 * - Single API call for efficiency
 */

import { useState, useEffect, useMemo } from 'react';
import { StatsOverview } from '@/components/Analytics/StatsOverview';
import { VolumeChart } from '@/components/Analytics/VolumeChart';
import { WorkoutTypePieChart } from '@/components/Analytics/WorkoutTypePieChart';
import { WeightComparison } from '@/components/Analytics/WeightComparison';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import './Analytics.css';

type ViewPeriod = 'week' | 'month';
type WorkoutType = 'Push' | 'Pull' | 'Leg' | 'Arms' | 'Cardio' | 'Other';

interface WorkoutLogSet {
  id: string;
  weight: number | null;
  reps: number | null;
}

interface WorkoutLog {
  id: string;
  started_at: string;
  completed_at: string | null;
  notes: string | null;
  workout_log_sets: WorkoutLogSet[];
}

export function Analytics(): JSX.Element {
  const { user } = useAuth();
  const [viewPeriod, setViewPeriod] = useState<ViewPeriod>('week');
  const [workouts, setWorkouts] = useState<WorkoutLog[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch all completed workouts once
  useEffect(() => {
    async function fetchWorkouts(): Promise<void> {
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('workout_logs')
        .select(`
          id,
          started_at,
          completed_at,
          notes,
          workout_log_sets (id, weight, reps)
        `)
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .order('started_at', { ascending: false });

      if (error) {
        console.error('Failed to fetch workouts:', error);
        setLoading(false);
        return;
      }

      setWorkouts(data ?? []);
      setLoading(false);
    }

    fetchWorkouts();
  }, [user]);

  // Calculate stats from fetched data
  const stats = useMemo(() => {
    const totalWorkouts = workouts.length;

    // Calculate average duration
    let totalDurationMinutes = 0;
    let workoutsWithDuration = 0;

    workouts.forEach((workout) => {
      if (workout.completed_at) {
        const start = new Date(workout.started_at).getTime();
        const end = new Date(workout.completed_at).getTime();
        const durationMinutes = Math.floor((end - start) / 60000);
        if (durationMinutes > 0 && durationMinutes < 480) {
          totalDurationMinutes += durationMinutes;
          workoutsWithDuration++;
        }
      }
    });

    const avgDuration = workoutsWithDuration > 0
      ? Math.round(totalDurationMinutes / workoutsWithDuration)
      : 0;

    return {
      totalWorkouts,
      avgDuration,
    };
  }, [workouts]);

  // Calculate volume data based on view period
  const volumeData = useMemo(() => {
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const counts: number[] = [0, 0, 0, 0, 0, 0, 0];

    const now = new Date();

    if (viewPeriod === 'week') {
      // Get current week boundaries (Sunday to Saturday)
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 7);

      workouts.forEach((workout) => {
        const date = new Date(workout.started_at);
        if (date >= startOfWeek && date < endOfWeek) {
          const dayIndex = date.getDay();
          counts[dayIndex] = (counts[dayIndex] ?? 0) + 1;
        }
      });

      return dayLabels.map((label, index) => ({
        label,
        value: counts[index] ?? 0,
      }));
    } else {
      // Monthly view: count workouts per day of week for the entire month
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      workouts.forEach((workout) => {
        const date = new Date(workout.started_at);
        if (date >= startOfMonth && date <= endOfMonth) {
          const dayIndex = date.getDay();
          counts[dayIndex] = (counts[dayIndex] ?? 0) + 1;
        }
      });

      return dayLabels.map((label, index) => ({
        label,
        value: counts[index] ?? 0,
      }));
    }
  }, [workouts, viewPeriod]);

  // Calculate workout type distribution for pie chart based on view period
  const workoutTypeData = useMemo(() => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date;

    if (viewPeriod === 'week') {
      // Current week boundaries (Sunday to Saturday)
      startDate = new Date(now);
      startDate.setDate(now.getDate() - now.getDay());
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 7);
    } else {
      // Current month boundaries
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    }

    const typeCounts: Record<string, number> = {};

    workouts.forEach((workout) => {
      const date = new Date(workout.started_at);
      if (date >= startDate && date <= endDate) {
        const type = workout.notes ?? 'Other';
        typeCounts[type] = (typeCounts[type] ?? 0) + 1;
      }
    });

    const colors: Record<string, string> = {
      Push: '#007aff',
      Pull: '#34c759',
      Leg: '#ff9500',
      Arms: '#af52de',
      Cardio: '#ff3b30',
      Other: '#8e8e93',
    };

    return Object.entries(typeCounts).map(([type, count]) => ({
      label: type,
      value: count,
      color: colors[type] ?? '#8e8e93',
    }));
  }, [workouts, viewPeriod]);

  // Calculate weight/duration data grouped by workout type
  const weightData = useMemo(() => {
    const workoutTypes: WorkoutType[] = ['Push', 'Pull', 'Leg', 'Arms', 'Cardio', 'Other'];
    const result: Record<WorkoutType, { workouts: { id: string; date: string; value: number }[]; average: number }> = {
      Push: { workouts: [], average: 0 },
      Pull: { workouts: [], average: 0 },
      Leg: { workouts: [], average: 0 },
      Arms: { workouts: [], average: 0 },
      Cardio: { workouts: [], average: 0 },
      Other: { workouts: [], average: 0 },
    };

    // Group workouts by type and calculate value for each
    workouts.forEach((workout) => {
      const type = (workout.notes as WorkoutType) ?? 'Other';
      const validType = workoutTypes.includes(type) ? type : 'Other';

      let value: number;

      if (validType === 'Cardio') {
        // For Cardio: use duration in minutes
        if (workout.completed_at) {
          const start = new Date(workout.started_at).getTime();
          const end = new Date(workout.completed_at).getTime();
          value = Math.round((end - start) / 60000); // Convert to minutes
        } else {
          value = 0;
        }
      } else {
        // For strength workouts: calculate total weight (weight * reps)
        value = workout.workout_log_sets.reduce((sum, set) => {
          const weight = set.weight ?? 0;
          const reps = set.reps ?? 0;
          return sum + (weight * reps);
        }, 0);
      }

      result[validType].workouts.push({
        id: workout.id,
        date: workout.started_at,
        value,
      });
    });

    // Calculate average for each type
    workoutTypes.forEach((type) => {
      const typeWorkouts = result[type].workouts;
      if (typeWorkouts.length > 0) {
        const total = typeWorkouts.reduce((sum, w) => sum + w.value, 0);
        result[type].average = Math.round(total / typeWorkouts.length);
      }
    });

    return result;
  }, [workouts]);

  if (loading) {
    return (
      <div className="analytics">
        <div className="analytics__header">
          <h1 className="analytics__title">Analytics</h1>
        </div>
        <div className="analytics__loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="analytics">
      <div className="analytics__header">
        <h1 className="analytics__title">Analytics</h1>
        <select
          className="analytics__period-selector"
          value={viewPeriod}
          onChange={(e) => setViewPeriod(e.target.value as ViewPeriod)}
        >
          <option value="week">Week</option>
          <option value="month">Month</option>
        </select>
      </div>

      <StatsOverview stats={stats} />

      <div className="analytics__section">
        <h2 className="analytics__section-title">
          {viewPeriod === 'week' ? 'Weekly' : 'Monthly'} Volume
        </h2>
        <VolumeChart data={volumeData} />
      </div>

      <div className="analytics__section">
        <h2 className="analytics__section-title">
          Workout Types ({viewPeriod === 'week' ? 'This Week' : 'This Month'})
        </h2>
        <WorkoutTypePieChart data={workoutTypeData} />
      </div>

      <div className="analytics__section">
        <h2 className="analytics__section-title">Weight by Workout Type</h2>
        <WeightComparison data={weightData} />
      </div>
    </div>
  );
}
